import IdGeneratorService from '@/services/id-generator.service';
import clsx from 'clsx';
import { Tooltip } from 'primereact/tooltip';
import { type Control, useForm, type UseFormReturn } from 'react-hook-form';
import { MdOutlineHelp } from 'react-icons/md';
import styles from './form.module.css';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { MasterCardIcon } from '../PaymentIcons';
import MaskInput from '@/components/inputs/MaskInput';
import { InputController } from '@/components/inputs/InputController';
import DropdownInput from '@/components/inputs/DropdownInput';
import { TextBodySmall } from '@/components/text/Texts';

export interface PaymentMethodFormType {
	CardNumber: string;
	ExpDate: string;
	Country: string;
	SecurityCode: string;
	consent: boolean;
}

type UsePaymentMethodFormOptions = {
	requireConsent?: boolean;
};

export const DIALOG_STATE = {
	LOADING: 'LOADING',
	ACTIVE: 'ACTIVE',
	UPLOADING: 'UPLOADING',
	ERROR: 'ERROR',
} as const;

export const usePaymentMethodForm = (
	{ requireConsent = false }: UsePaymentMethodFormOptions = {},
): UseFormReturn<PaymentMethodFormType> => {
	return useForm<PaymentMethodFormType>({
		defaultValues: {
			CardNumber: '',
			ExpDate: '',
			Country: 'Philippines',
			SecurityCode: '',
			consent: !requireConsent,
		},
		resolver: yupResolver(yup.object({
			CardNumber: yup
				.string()
				.required(VALIDATION_MESSAGES.required)
				.test('cardNumber', 'Card number must be 16 digits', (value) => {
					if (!value) return false;
					const digitsOnly = value.replace(/\s/g, '');
					return /^\d{16}$/.test(digitsOnly);
				}),
			ExpDate: yup
				.string()
				.required(VALIDATION_MESSAGES.required)
				.matches(/^(0[1-9]|1[0-2])\/\d{2}$/, VALIDATION_MESSAGES.incorrect_exp_date)
				.test('expDate', VALIDATION_MESSAGES.incorrect_exp_date, (value) => {
					if (!value) return false;

					const [monthStr, yearStr] = value.split('/');
					const month = parseInt(monthStr, 10);
					const year = parseInt(yearStr, 10);
					if (isNaN(month) || isNaN(year)) return false;

					const fullYear = 2000 + year;

					const now = new Date();
					const currentMonth = now.getMonth() + 1;
					const currentYear = now.getFullYear();

					return fullYear > currentYear || (fullYear === currentYear && month >= currentMonth);
				}),
			Country: yup.string().required(),
			SecurityCode: yup
				.string()
				.required(VALIDATION_MESSAGES.required)
				.matches(/^\d{3}$/, 'Security code must be 3 digits'),
			consent: requireConsent
				? yup.boolean().oneOf([true]).required()
				: yup.boolean().required(),
		})),
		mode: 'all',
	});
};

type Props = {
	className?: string;
	gapSize?: 'normal' | 'wide';
	cvvLabel?: string;
	control: Control<PaymentMethodFormType>;
};

const PaymentMethodForm = ({
	className,
	gapSize = 'normal',
	cvvLabel = 'CVV',
	control,
}: Props) => {
	const formId = 'payment-method';


	return (
		<form
			className={clsx(
				className,
				'flex flex-col',
				gapSize === 'normal' && 'gap-4',
				gapSize === 'wide' && 'gap-8',
			)}
			id={new IdGeneratorService(formId).formId}
		>
			<InputController id="CardNumber" control={control}>
				<MaskInput
					label="Card Number"
					mask="9999 9999 9999 9999"
					icon={(<MasterCardIcon />)}
				/>
			</InputController>

			<div
				className={clsx('flex', {
					'gap-3': gapSize === 'normal',
					'gap-6': gapSize === 'wide',
				})}
			>
				<InputController id="ExpDate" control={control}>
					<MaskInput
						label="Exp date"
						slotChar="MM/YY"
						mask="99/99"
					/>
				</InputController>
				<Tooltip
					target="#securityCode-info"
					position="top"
					pt={{
						root: { className: 'p-0' },
						text: { className: 'w-[200px] bg-neutral30 px-2 py-1 flex flex-col justify-between' },
					}}
				>
					<>
						<TextBodySmall
							className="text-inverseOnSurface"
							text="Security code is a 3 digit number that is found on the back of your card."
						/>
					</>
				</Tooltip>
				<InputController id="SecurityCode" control={control}>
					<MaskInput
						label={cvvLabel}
						mask="999"
						icon={(<MdOutlineHelp
							id="securityCode-info"
							size={20}
							className="text-black cursor-pointer" />)}
					/>
				</InputController>

				<Tooltip
					target=".code-help-icon"
					position="bottom"
					className="bg-inverseSurface max-w-[200px] rounded-[4px]"
					pt={{
						text: () => ({
							className: styles.codeTooltipText,
						}),
					}}
				>
					<p className="text-inverseOnSurface text-sm">
						Security code is a 3 digit number that is found on the
						back of your card.
					</p>
				</Tooltip>
			</div>


			<InputController id="Country" control={control}>
				<DropdownInput
					disabled
					options={["Philippines"]}
					label="Country"
				/>
			</InputController>
		</form>
	);
};

export default PaymentMethodForm;
